import { useDebouncedCallback, useResizeObserver, useSyncedRef } from "@react-hookz/web";
import { useAtom } from "jotai";
import { useEffect, useRef, useState } from "react";

import { useScrollOverThreshold } from "scmp-app/lib/hooks/use-scroll-over-threshold";

import type { AppBarSticky } from "./atoms";
import { appBarStickyAtom } from "./atoms";

/**
 * The AppBar's sticky-height tracking contract, derived from the sticky style the
 * AppBar applies to its container. Restores the observable behaviour of the
 * pre-sentinel implementation (`SCMPWEB-10011` review Issue 1), where
 * `stickyHeight = rect.height + rect.top` while the container is in its sticky
 * shape (`position: sticky`) and 0 otherwise:
 *
 * - `withHideElementAfterScrollThreshold`: the container is `position: sticky` from
 *   mount and flips to `translateY(-translateDistancePx)` at `flipThresholdPx`
 *   (`HideElementOnScrollEffectThreshold`) — before the flip the container is fully
 *   visible (`appBarHeight`), after the flip only `appBarHeight − translateDistancePx`
 *   stays on screen (the sliver is translated away).
 * - `withScrollAwayElement`: the container starts `position: relative` (0), then flips
 *   to `position: sticky` + `translateY(-translateDistancePx)` at
 *   `appBarStickyScrollHeight` — after the flip only the post-translate remainder
 *   (`appBarHeight − translateDistancePx`) stays on screen.
 * - `null`: the container never becomes sticky (game pages render it as
 *   `position: relative` with no translate) — `stickyHeight` stays 0 while
 *   `appBarHeight` still tracks the container size.
 */
export type AppBarStickyHeightContract = null | {
  flipThresholdPx: number;
  notFlippedValue: 0 | "appBarHeight";
  translateDistancePx: number;
};

export const useAppBarSetStickyHeight = (contract: AppBarStickyHeightContract) => {
  const [appBarSticky, setAppBarSticky] = useAtom(appBarStickyAtom);
  const latestAppBarSticky = useSyncedRef(appBarSticky);
  const updateAppBarSticky = useDebouncedCallback(
    (appBarSticky: AppBarSticky) => setAppBarSticky(appBarSticky),
    [setAppBarSticky],
    100,
  );

  const writeStickyState = (appBarHeight: number, stickyHeight: number) => {
    // Avoid update the atom if the values are the same
    if (
      latestAppBarSticky.current?.appBarHeight === appBarHeight &&
      latestAppBarSticky.current?.stickyHeight === stickyHeight
    )
      return;
    updateAppBarSticky({ appBarHeight, stickyHeight });
  };

  // Sentinel flip at the same scroll threshold as the variant's style hook — the shared
  // threshold-keyed registry dedupes identical thresholds with the AppBar's own
  // `useStickyWith*` hooks. `null` (game pages) subscribes to nothing.
  const flipThresholdPx = contract ? contract.flipThresholdPx : null;
  const translated = useScrollOverThreshold(flipThresholdPx);

  // Last container height measured by the resize path. The flip path derives its value
  // from this instead of doing a layout read on the scroll path.
  const [appBarHeight, setAppBarHeight] = useState(0);

  // Master parity value derivation — identical to the old per-tick
  // `rect.height + rect.top` once settled: `rect.top` is `−translateDistancePx` while
  // translated and 0 (or "container fully visible") before it.
  const derivedStickyHeight = contract
    ? translated
      ? appBarHeight - contract.translateDistancePx
      : contract.notFlippedValue === "appBarHeight"
        ? appBarHeight
        : contract.notFlippedValue
    : 0;

  const isHideVariant = contract?.notFlippedValue === "appBarHeight";

  const reference = useRef<HTMLElement>(null);
  const handleResizeReference = useRef<() => void>(() => {});
  useEffect(() => {
    // Resize path (event-driven, not on the scroll path): mirrors the old
    // implementation's resize behaviour exactly — the value is the live
    // `rect.height + rect.top`:
    // - hide variant: the container is `position: sticky` from mount, so the value is
    //   live at all times (`rect.top` is 0 before the translate flips,
    //   `−translateDistancePx` after).
    // - scroll-away variant: the container is `position: relative` until the flip
    //   (value 0), then `position: sticky` with the translate applied.
    handleResizeReference.current = () => {
      const element = reference.current;
      if (!element) return;
      const rect = element.getBoundingClientRect();
      setAppBarHeight(rect.height);
      writeStickyState(
        rect.height,
        !contract || (!isHideVariant && !translated) ? 0 : rect.height + rect.top,
      );
    };
  });

  useResizeObserver(reference, _ => {
    handleResizeReference.current();
  });

  // Flip / translate-distance-change path: re-derive the settled value without any
  // layout read — at the flip point the translate has fully applied, so the settled
  // value matches the old throttled-tick steady state (`rect.height − translateDistance`).
  useEffect(() => {
    writeStickyState(appBarHeight, derivedStickyHeight);
    // writeStickyState identity is stable (debounced callback); recompute is driven by
    // the derived values only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appBarHeight, derivedStickyHeight]);

  return {
    reference,
  };
};
